//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import ArrayStore from 'devextreme/data/array_store'

//#endregion


import { ColumnModel } from '../../modules/api/api.models'

import { Enums } from '../../models/common/enums'
import { OpMeter, OpData, OpDevice, OpView, OpViewColumn, OpOperator, OpLocation } from '../../models/core'
import { GetViewColumnOptions } from '../../models/core/op-view'
import { ObjectCustomString } from '../../models/util'

import { MeterEditInitAdditionalParams } from '../../modules/meters'

import { ToastHelper, LocalizationHelper, CommonHelper, ConvertHelper, LogHelper, TypeHelper, MapHelper } from '../../helpers'

import { CreateEditInitParams, BulkEditInitParams } from '../../modules/common/create-edit-popup'
import { GridTypes } from '../../modules/common/grid/grid.controller'
import { TreeListOptions } from '../../modules/common/tree-list/tree-list.controller'

import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'
import { GridService, GRID_SERVICE_NAME } from '../grid'
import { SYSTEM_SERVICE_NAME, SystemService, DetailsOptions, CreateEditResult, DetailsResult } from '../system'

// #region Consts

export const METER_SERVICE_NAME: string = 'MeterService'
export const PROVIDER_NAME: string = 'MetereServiceProvider'

// #endregion

export class MeterService {

    // #region Consts

    public static get FILE_NAME_PREFIX(): string { return 'meters' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdMeter }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Meter }
    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Meter]' }

    public static get TreeKeyFieldName() { return 'idTreeItem' }
    public static get TreeParentFieldName() { return 'idParentTreeItem' }

    // #endregion

    // #region Ctor

    constructor(
        private readonly gridService: GridService,
        private readonly systemService: SystemService,
        private readonly userSession: UserSessionService
    ) {
    }

    // #endregion

    // #region Methods

    public getMeter(idMeter: number): JQueryPromise<OpMeter> {
        return this.userSession
            .getDataProvider()
            .Meter
            .getMeter(idMeter)
            .then((meter: OpMeter) => {
                return meter
            })
    }


    public deleteMeter(idMeter: number): JQueryPromise<void> {
        const deferred: JQueryDeferred<void> = $.Deferred<void>()
        const toDelete: OpMeter = new OpMeter(null)

        toDelete.idMeter = idMeter
        toDelete.opState = Enums.OpChangeState.Delete

        this.userSession
            .getDataProvider()
            .Meter
            .deleteMeter(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, MeterService.SIMAX_MODULE_NAME))
                deferred.resolve()
            })
            .fail((error: any) => {

                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(MeterService.LOG_PREFIX, `Unnable to delete meter with id ${toDelete.idMeter}`, error)

                deferred.resolve(error)
            })

        return deferred.promise()
    }

    public saveMeter(meter: OpMeter, isBulkSave: boolean = false): JQueryPromise<OpMeter> {
        const deferred: JQueryDeferred<OpMeter> = $.Deferred<OpMeter>()

        this.userSession
            .getDataProvider()
            .Meter
            .saveMeter(meter)
            .then((savedObject: OpMeter) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, MeterService.SIMAX_MODULE_NAME))
                }

                deferred.resolve(savedObject)
            })
            .fail((error: any) => {
                if (!isBulkSave) {
                    ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                    LogHelper.warn(MeterService.LOG_PREFIX, `Unnable to save meter with id ${meter.idMeter}`, error)
                }

                deferred.reject(error)
            })

        return deferred.promise()
    }

    public saveMeterDevices(
        opMeter: OpMeter,
        added: { id: number, [key: string]: any }[],
        removed: { id: number, [key: string]: any }[],
        idDepository: number): JQueryPromise<OpMeter> {
        const newDevices: OpDevice[] = added.map((addedDevice: any) => {
            const device: OpDevice = new OpDevice(null)

            device.serialNbr = addedDevice.id
            device.opState = Enums.OpChangeState.New

            return device
        })

        const removedDevicesIds: number[] = []
        const removedDevices: OpDevice[] = removed.map((removedDevice: any) => {
            const device: OpDevice = new OpDevice(null)

            device.serialNbr = removedDevice.id
            device.opState = Enums.OpChangeState.Delete
            removedDevicesIds.push(device.serialNbr)

            const depository: OpLocation = new OpLocation(null)

            if (idDepository != null) {
                depository.idLocation = idDepository
            } else if (this.userSession.getLoggedOperator().depositoryLocation != null) {
                depository.idLocation = this.userSession.getLoggedOperator().depositoryLocation.idLocation
            }

            depository.opState = Enums.OpChangeState.New
            device.currentLocations.push(depository)

            return device
        })

        const loadedDevices: OpDevice[] = opMeter.currentDevices
            .filter((device: OpDevice) => removedDevicesIds.indexOf(device.serialNbr) === -1)
            .map((loadedDevice: OpDevice) => {
                const device: OpDevice = new OpDevice(null)

                device.serialNbr = loadedDevice.serialNbr
                device.opState = Enums.OpChangeState.Loaded

                return device
            })

        const meterToSave: OpMeter = new OpMeter(null)
        meterToSave.idMeter = opMeter.idMeter
        meterToSave.opState = Enums.OpChangeState.Modified
        meterToSave.currentDevices = loadedDevices.concat(...newDevices, ...removedDevices)
        meterToSave.currentLocations = opMeter.currentLocations

        if (_.size(meterToSave.currentLocations) == 0) {
            if (meterToSave.currentLocations == null) {
                meterToSave.currentLocations = []
            }

            const operator: OpOperator = this.userSession.getLoggedOperator()
            const location: OpLocation = new OpLocation(null)
            location.opState = Enums.OpChangeState.New

            if (operator.depositoryLocation != null) {
                location.idLocation = operator.depositoryLocation.idLocation
            } else if (operator.idDepositoryLocation != null) {
                location.idLocation = operator.idDepositoryLocation
            }

            if (location.idLocation != null) {
                meterToSave.currentLocations.push(location)
            } else {
                const deferred: JQueryDeferred<OpMeter> = $.Deferred<OpMeter>()
                ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CantSaveMeterNoLocation))
                deferred.reject()
                return deferred.promise()
            }
        }
        return this.saveMeter(meterToSave)
    }

    // #endregion

    //#region CreateEdit

    public openBulkEdit(columns: any[], selectedGridItems: any[]): JQueryPromise<CreateEditResult<OpMeter>> {
        const bulkEditInitParams: BulkEditInitParams = this.getBulkEditInitOptions(columns, selectedGridItems)

        return this.systemService.openBulkCreateEdit(bulkEditInitParams);
    }

    public openCreate(templateId: number = null, targetLocation: OpLocation = null): JQueryPromise<CreateEditResult<OpMeter>> {
        const createEditOptions: CreateEditInitParams<MeterEditInitAdditionalParams> = this.getCreateEditOptions(templateId || 0, undefined, undefined, !!templateId)

        createEditOptions.additionalParameters = {
            destinationLocation: targetLocation != null ? targetLocation.clone(false, false, false, false) : null
        }

        return this.systemService.openCreateEdit<OpMeter>(createEditOptions);
    }

    public openEdit(idMeter: number, idMeterTypeClass: number): JQueryPromise<CreateEditResult<OpMeter>> {
        const createEditOptions: CreateEditInitParams<MeterEditInitAdditionalParams> = this.getCreateEditOptions(idMeter, undefined, undefined, false, idMeterTypeClass)

        return this.systemService.openCreateEdit<OpMeter>(createEditOptions);
    }

    public openEditByMeter(meter: OpMeter): JQueryPromise<CreateEditResult<OpMeter>> {
        if (meter != null) {
            const idMeterTypeClass: number = meter.meterType ? meter.meterType.idMeterTypeClass : null
            const createEditOptions: CreateEditInitParams<MeterEditInitAdditionalParams> = this.getCreateEditOptions(meter.idMeter, meter, undefined, false, idMeterTypeClass)

            return this.systemService.openCreateEdit<OpMeter>(createEditOptions);
        }

        return null;
    }

    public openExtendedParametersEditByMeter(meter: OpMeter): JQueryPromise<CreateEditResult<OpMeter>> {
        if (meter != null) {
            const idMeterTypeClass: number = meter.meterType ? meter.meterType.idMeterTypeClass : null
            const createEditOptions: CreateEditInitParams<MeterEditInitAdditionalParams> = this.getCreateEditOptions(meter.idMeter, meter, Enums.SimaxCreateEditSelectedTabType.ExtendedParameters, false, idMeterTypeClass)

            return this.systemService.openCreateEdit<OpMeter>(createEditOptions);
        }

        return null;
    }

    public getCreateEditOptions(referenceObjectId: number = 0,
        referenceObject: OpMeter = null,
        selectedTabType: Enums.SimaxCreateEditSelectedTabType = Enums.SimaxCreateEditSelectedTabType.BaseParameters,
        isCopy: boolean = false,
        dataTypeGroupReferenceValue: number = null
    ): CreateEditInitParams {
        return {
            createEditSelectedTabType: selectedTabType,
            groupReferenceType: Enums.ReferenceType.IdMeterTypeClass,
            groupReferenceValue: referenceObject != null && referenceObject.meterType != null ? referenceObject.meterType.idMeterTypeClass : dataTypeGroupReferenceValue != null ? dataTypeGroupReferenceValue : null,
            idReferenceType: MeterService.REFERENCE_TYPE,
            moduleName: MeterService.SIMAX_MODULE_NAME,
            referenceObject: null,
            referenceObjectId: referenceObjectId,
            sections: null,
            useBusinessObjects: true,
            isCopy: isCopy
        }
    }
    
    public getBulkEditInitOptions(columns: any[], selectedGridItems: any[]): BulkEditInitParams {
        return {
            gridColumns: columns,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: MeterService.REFERENCE_TYPE,
            moduleName: MeterService.SIMAX_MODULE_NAME,
            section: null,
            selectedGridRows: TypeHelper.clone(selectedGridItems),
            useBusinessObjects: true
        }
    }

    //#endregion

    //#region Details

    public openDetails(idMeter: number, pageItemsIds: number[]): JQueryPromise<DetailsResult> {
        const detailsOptions: DetailsOptions = this.getDetailsOptions(idMeter);

        detailsOptions.pageItemsIds = pageItemsIds;

        return this.systemService.openDetails(detailsOptions);
    }

    public getDetailsOptions(idMeter: number): DetailsOptions {
        return {
            hasPermission: true,
            idReferenceType: Enums.ReferenceType.IdMeter,
            moduleName: MeterService.SIMAX_MODULE_NAME,
            pageItemsIds: null,
            referenceObjectId: idMeter,
        }
    }

    //#endregion

    // #region Options
    public getMainGridColumns(): JQueryPromise<ColumnModel[]> {
        return this.gridService
            .getColumnModelByCategory(
                [
                    Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
                    Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
                ],
                MeterService.SIMAX_MODULE_NAME,
                {
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: this.userSession.getLoggedOperator().idDistributor
                }
            )
    }

    public getExtededParametersGridColumns(): ColumnModel[] {
        const descriptionColumn: ColumnModel = new ColumnModel()
        descriptionColumn.idName = 'dataType'
        descriptionColumn.name = 'DataType'
        descriptionColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description)
        descriptionColumn.type = Enums.DataTypeClass.Text

        const indexColumn: ColumnModel = new ColumnModel()
        indexColumn.idName = 'indexNbr'
        indexColumn.name = 'Index'
        indexColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Index)
        indexColumn.type = Enums.DataTypeClass.Text

        const valueColumn: ColumnModel = new ColumnModel()
        valueColumn.idName = 'value'
        valueColumn.name = 'Value'
        valueColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Value)
        valueColumn.type = Enums.DataTypeClass.Text

        const unitColumn: ColumnModel = new ColumnModel()
        unitColumn.idName = 'unit'
        unitColumn.name = 'Unit'
        unitColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Unit)
        unitColumn.type = Enums.DataTypeClass.Text

        return [descriptionColumn, indexColumn, valueColumn, unitColumn]
    }

    public getExtendedParametersOptions(showLegend: boolean = true): TreeListOptions {
        const options: TreeListOptions = {
            name: 'MetersDetails',
            type: GridTypes.NO_SEARCH_BAR_SMALL,
            buttonsOptions: {
                enabled: showLegend,
            },

            addCustomFilters: true,
            columnsDictionary: this.getExtededParametersGridColumns(),
            dataSource: [],
            showDatatypeLegend: showLegend,
            localStorageVarForSavedEntity: 'meterSavedParamGrid',

            keyExpr: MeterService.TreeKeyFieldName,
            parentIdExpr: MeterService.TreeParentFieldName
        }

        return options
    }

    public createDataSourceForExtendedParameters(meter: OpMeter, allowedDataTypes: number[]): ArrayStore {
        const items: any[] = []

        if (meter && _.size(meter.dataList) > 0) {
            const filteredData: OpData[] = meter.dataList.toArray().filter((dItem: OpData) => allowedDataTypes.some((idDataType: number) => dItem.idDataType == idDataType))

            let treeItemId: number = +new Date()

            for (let dItem of filteredData) {
                if (dItem.dataType == null) {
                    dItem.dataType = this.userSession
                        .getDataProvider()
                        .DataType
                        .getDataTypeLocal(dItem.idDataType)
                }

                const treeItem: any = dItem
                treeItem[MeterService.TreeKeyFieldName] = treeItemId++
                treeItem[MeterService.TreeParentFieldName] = 0
                items.push(treeItem)
            }

            const grouping: _.Dictionary<OpData[]> = _.groupBy(filteredData, 'idDataType')

            if (grouping) {
                for (let groupKey in grouping) {
                    const groupedData: OpData[] = grouping[groupKey]
                    if (groupedData.length > 1) {

                        const sourceItem: OpData = groupedData[0]

                        const groupingRowItem: any = {
                            dataType: sourceItem.dataType,                             // dataType column
                            indexNbr: groupedData.map(row => row.indexNbr).join(';'),  // indexNbr column
                            unit: sourceItem.unit,                                     // unit column
                            rows: groupedData,                                         // child rows
                            typeId: sourceItem.dataType.idDataTypeClass,               // ???
                        }

                        groupingRowItem[MeterService.TreeKeyFieldName] = sourceItem.idDataType
                        groupingRowItem[MeterService.TreeParentFieldName] = 0

                        items.push(groupingRowItem)

                        groupedData.forEach((row: OpData) => {
                            row[MeterService.TreeParentFieldName] = sourceItem.idDataType
                        })
                    }
                }

                items.push
            }
        }

        return new ArrayStore({
            key: MeterService.TreeKeyFieldName,
            data: items
        })
    }

    public isMetersSameClass(opMeters: OpMeter[]): boolean {
        if (_.size(opMeters) > 0) {
            if (opMeters.every((meter: OpMeter) => meter.idMeterType == opMeters[0].idMeterType)) {
                return true
            } else {
                ToastHelper.showWarning(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlarmDefsAllMetersSameClassWarning))
                return false
            }
        }

        return true
    }

    public getMainGridDataSourceParser(): (meter: OpMeter) => { additionalFields: { key: string, value: any }[] } {
        return (meter: OpMeter): { additionalFields: { key: string, value: any }[] } => {
            if (meter == null) {
                return null
            }

            const additionalFieldsObj: { additionalFields: { key: string, value: any }[] } = {
                additionalFields: []
            }

            // LAT LONG COLS
            if (meter.dynamicValues != null) {
                const view: OpView = this.userSession
                    .getDataProvider()
                    .View
                    .getActiveViewLocal(MeterService.REFERENCE_TYPE)

                let options: GetViewColumnOptions

                options = new GetViewColumnOptions(Enums.DataType.HELPER_ID_LOCATION)
                options.keyColumn = view.getPrimaryKeyColumn()
                options.viewColumnType = Enums.ViewColumnType.BaseHelper

                const locationColumn: OpViewColumn = view.getViewColumn(options)

                if (locationColumn != null) {
                    options = new GetViewColumnOptions(Enums.DataType.LOCATION_LATITUDE)
                    options.keyColumn = locationColumn
                    options.viewColumnType = Enums.ViewColumnType.Standard

                    const latitudeColumn: OpViewColumn = view.getViewColumn(options)

                    options = new GetViewColumnOptions(Enums.DataType.LOCATION_LONGITUDE)
                    options.keyColumn = locationColumn
                    options.viewColumnType = Enums.ViewColumnType.Standard

                    const longitudeColumn: OpViewColumn = view.getViewColumn(options)

                    if (latitudeColumn != null && longitudeColumn != null) {
                        const latitudeKv: any = _.find(meter.dynamicValues, ((dynamicValue: any) => dynamicValue.Key === latitudeColumn.name));
                        const longitudeKv: any = _.find(meter.dynamicValues, ((dynamicValue: any) => dynamicValue.Key === longitudeColumn.name))

                        additionalFieldsObj.additionalFields.push({
                            key: MapHelper.LatitudeFieldName,
                            value: latitudeKv == null ? null : latitudeKv.Value
                        })

                        additionalFieldsObj.additionalFields.push({
                            key: MapHelper.LongitudeFieldName,
                            value: longitudeKv == null ? null : longitudeKv.Value
                        })
                    }
                }
            }

            // MARKER TOOLTIP
            const markerFormat: string = ObjectCustomString.getCutomMarkerStringFormat(meter)

            if (markerFormat != null) {
                additionalFieldsObj.additionalFields.push({
                    key: MapHelper.MarkerStringFieldName,
                    value: ObjectCustomString.getObjectCustomString(markerFormat, meter)
                })
            }

            // MARKER COLOR
            if (meter.dataList != null && meter.dataList.length > 0) {

                additionalFieldsObj.additionalFields.push({
                    key: MapHelper.MarkerColorFieldName,
                    value: meter.dataList.getValue(Enums.DataType.HELPER_COLOR)
                })
            }

            return additionalFieldsObj
        }
    }

    // #endregion
}

export class MeterServiceProvider implements ng.IServiceProvider {

    // #region IServiceProvider Implementation 

    public $get = [GRID_SERVICE_NAME, SYSTEM_SERVICE_NAME, USER_SESSION_SERVICE_NAME, function (gridService: GridService, systemService: SystemService, userSession: UserSessionService) {
        return new MeterService(gridService, systemService, userSession)
    }]

    // #endregion
} 